import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import AuthCallback from "@/pages/AuthCallback";
import Login from "@/pages/Login";
import Register from "@/pages/Register";
import TransferReceipt from "@/pages/TransferReceipt";
import { PinVerify, PinChangeForced, DeviceConflict } from "@/pages/PinScreen";
import AdminRegistrations from "@/admin/AdminRegistrations";
import AdminLogs from "@/admin/AdminLogs";
import AdminReports from "@/admin/AdminReports";
import AdminFeeReport from "@/admin/AdminFeeReport";
import { SuperPinPrompt } from "@/components/SuperPinPrompt";
import { isStaff, rank } from "@/lib/format";
import MobileShell from "@/components/MobileShell";
import Home from "@/pages/Beranda";
import Savings from "@/pages/Savings";
import Loans from "@/pages/Loans";
import LoanApply from "@/pages/LoanApply";
import Transactions from "@/pages/Transactions";
import Payments from "@/pages/Payments";
import Transfer from "@/pages/Transfer";
import Notifications from "@/pages/Notifications";
import Profile from "@/pages/Profile";
import AdminLayout from "@/admin/AdminLayout";
import AdminDashboard from "@/admin/AdminDashboard";
import AdminMembers from "@/admin/AdminMembers";
import AdminLoans from "@/admin/AdminLoans";
import AdminRequests from "@/admin/AdminRequests";
import AdminSettings from "@/admin/AdminSettings";

const Loader = () => (
  <div className="min-h-screen flex items-center justify-center bg-slate-50" data-testid="app-loading">
    <div className="h-10 w-10 rounded-full border-4 border-red-600 border-t-transparent animate-spin" />
  </div>
);

function AdminOnly({ children }) {
  const { user } = useAuth();
  return rank(user) >= 2 ? children : <Navigate to="/admin" replace />;
}

function Protected({ children, admin }) {
  const { user, loading } = useAuth();
  if (loading) return <Loader />;
  if (!user) return <Navigate to="/" replace />;
  if (!user.pin_verified) return <PinVerify />;
  if (user.device_ok === false) return <DeviceConflict />;
  if (user.must_change_pin) return <PinChangeForced />;
  if (admin && !isStaff(user)) return <Navigate to="/app" replace />;
  return children;
}

function AppRouter() {
  const location = useLocation();
  const { user, loading } = useAuth();
  if (location.hash?.includes("session_id=")) return <AuthCallback />;
  return (
    <Routes>
      <Route path="/" element={loading ? <Loader /> : user ? <Navigate to="/app" replace /> : <Login />} />
      <Route path="/daftar" element={<Register />} />
      <Route path="/app" element={<Protected><MobileShell /></Protected>}>
        <Route index element={<Home />} />
        <Route path="tabungan" element={<Savings />} />
        <Route path="pinjaman" element={<Loans />} />
        <Route path="pinjaman/ajukan" element={<LoanApply />} />
        <Route path="transaksi" element={<Transactions />} />
        <Route path="transaksi/bayar" element={<Payments />} />
        <Route path="transaksi/transfer" element={<Transfer />} />
        <Route path="transaksi/bukti/:id" element={<TransferReceipt />} />
        <Route path="notifikasi" element={<Notifications />} />
        <Route path="profil" element={<Profile />} />
      </Route>
      <Route path="/admin" element={<Protected admin><AdminLayout /></Protected>}>
        <Route index element={<AdminDashboard />} />
        <Route path="anggota" element={<AdminMembers />} />
        <Route path="pinjaman" element={<AdminLoans />} />
        <Route path="permintaan" element={<AdminRequests />} />
        <Route path="pendaftaran" element={<AdminRegistrations />} />
        <Route path="log" element={<AdminOnly><AdminLogs /></AdminOnly>} />
        <Route path="kinerja" element={<AdminOnly><AdminReports /></AdminOnly>} />
        <Route path="pendapatan" element={<AdminOnly><AdminFeeReport /></AdminOnly>} />
        <Route path="pengaturan" element={<AdminOnly><AdminSettings /></AdminOnly>} />
      </Route>
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

export default function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <AppRouter />
        <Toaster position="top-center" richColors />
        <SuperPinPrompt />
      </AuthProvider>
    </BrowserRouter>
  );
}
